import type {FC} from 'react';
import {useEffect, useId, useState} from 'react';
import {useTheme} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import {Theme} from '@wellstone-solutions/common';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Icon,
  IconButton,
  IconNames,
  Stack,
  Typography,
} from '@wellstone-solutions/web';
import type {ContactNote} from 'api/v2';
import {DATE_TIME_FORMAT_LONG} from '../../constants';
import {ContactNoteFacts, ContactNoteSummary} from '../ContactNoteForm';
import {formatDate} from '../NoteForm';
import {copyNarrative} from './copyNarrative';

const COPIED_FOR_MS = 1800;

const localTimeZoneName = () =>
  new Intl.DateTimeFormat(undefined, {timeZoneName: 'short'})
    .formatToParts(new Date())
    .find((part) => part.type === 'timeZoneName')?.value ?? '';

const stamp = (value: string) => formatDate(value, DATE_TIME_FORMAT_LONG);

type CopyNarrativeButtonProps = {
  narrative: string;
};

const CopyNarrativeButton: FC<CopyNarrativeButtonProps> = ({narrative}) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return undefined;
    }
    const timer = setTimeout(() => setCopied(false), COPIED_FOR_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <Button
      variant="outlined"
      size="small"
      startIcon={
        <Icon name={copied ? IconNames.Check : IconNames.Copy} size={18} />
      }
      onClick={async () => setCopied(await copyNarrative(narrative))}>
      {copied ? 'Copied' : 'Copy narrative'}
    </Button>
  );
};

type HeaderNameProps = {
  label: string;
  name: string;
};

const HeaderName: FC<HeaderNameProps> = ({label, name}) => (
  <Typography
    component="span"
    sx={{
      fontSize: 20,
      fontWeight: 700,
      lineHeight: '28px',
      color: Theme.colorPalette.primary,
    }}>
    <Box component="span" sx={{fontWeight: 400, color: 'text.secondary'}}>
      {label}:
    </Box>{' '}
    {name}
  </Typography>
);

type HeaderProps = {
  note: ContactNote;
  titleId: string;
  onClose: () => void;
};

const Header: FC<HeaderProps> = ({note, titleId, onClose}) => (
  <Stack spacing={2} sx={{px: {xs: 2, sm: 3}, pt: 2.5}}>
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Stack
        id={titleId}
        component="h2"
        direction="row"
        flexWrap="wrap"
        columnGap={4}
        rowGap={0.5}
        sx={{flex: 1, minWidth: 0, m: 0}}>
        <HeaderName label="Member" name={note.member.name} />
        <HeaderName label="Staff" name={note.staff.name} />
      </Stack>
      <IconButton
        aria-label="Close"
        icon={IconNames.Xmark}
        onClick={onClose}
        sx={{width: 40, height: 40, color: 'text.secondary'}}
      />
    </Stack>
    <ContactNoteFacts note={note} />
  </Stack>
);

type FooterProps = {
  note: ContactNote;
};

const Footer: FC<FooterProps> = ({note}) => (
  <Stack
    direction="row"
    flexWrap="wrap"
    columnGap={2.5}
    rowGap={0.5}
    sx={{
      px: {xs: 2, sm: 3},
      py: 1.5,
      fontSize: 12,
      lineHeight: '16px',
      color: 'text.secondary',
      bgcolor: 'grey.50',
      borderTop: 1,
      borderColor: 'divider',
    }}>
    <span>Created {stamp(note.created)}</span>
    {/* The API stamps created and modified microseconds apart on a new note */}
    {stamp(note.modified) !== stamp(note.created) && (
      <span>Modified {stamp(note.modified)}</span>
    )}
    <span>Form v{note.definitionVersion}</span>
    <span>Local time ({localTimeZoneName()})</span>
  </Stack>
);

type NoteDetailsDialogProps = {
  note: ContactNote;
  open: boolean;
  onClose: () => void;
};

export const NoteDetailsDialog: FC<NoteDetailsDialogProps> = ({
  note,
  open,
  onClose,
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const titleId = useId();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isPhone}
      fullWidth
      maxWidth="md"
      aria-labelledby={titleId}
      PaperProps={{
        elevation: 8,
        sx: {
          maxHeight: {sm: 'min(860px, calc(100% - 64px))'},
          borderRadius: {sm: 2},
        },
      }}>
      <Header note={note} titleId={titleId} onClose={onClose} />
      <DialogContent sx={{px: {xs: 2, sm: 3}, pt: 2.5, pb: 3}}>
        <ContactNoteSummary
          note={note}
          narrativeAction={<CopyNarrativeButton narrative={note.narrative} />}
        />
      </DialogContent>
      <Footer note={note} />
    </Dialog>
  );
};
